import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Collapse, Grid } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SensorCard } from '../../components/Sensors/SensorCard.js';
import DevicesListFilter from '../../components/DevicesFilter/DevicesListFilter';
import MDTypography from '../../components/MDTypography';
import { filterDevices } from '../../helpers/filterHelpers';
import { fetchUserDevices, setCurrentPage } from '../../stores/sensor/sensorStore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CustomSnackbar from '../../components/CustomSnackbar';
import MDBox from 'components/MDBox';
import DashboardNavbar from '../../examples/Navbars/DashboardNavbar';
import DashboardLayout from '../LayoutContainers/DashboardLayout';

const REFRESH_TIME_IN_SECONDS = 300;

const ManageSensorsContent = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.sensors.currentUser);
  const devices = useSelector(state => state.sensors.devices);
  const { selectedFilterObject } = useSelector(state => state.persistence);
  const { distributor, customer, building, floor, area, hvacEquipment, status, searchString } = selectedFilterObject;
  // const searchString = useSelector(state => state.persistence.searchString || '');
  // console.log('devices:', devices);
  const [snackbarState, setSnackbarState] = useState({
    snackbarOpen: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const [unassignedOpen, setUnassignedOpen] = useState(true);
  const [assignedOpen, setAssignedOpen] = useState(true);
  const { vertical, horizontal, snackbarOpen } = snackbarState;

  // UNASSIGNED devices: Never filtered, always show all
  const unassignedDevices = useMemo(() => {
    const unassigned = devices.filter(device => device.building === null || device.building === '');
    if (!searchString) return unassigned;
    const lower = searchString.toLowerCase();
    return unassigned.filter(
      device => device.name?.toLowerCase().includes(lower) || device.id?.toString().includes(searchString),
    );
  }, [devices, searchString]);
  // Assigned devices: apply all filters.
  const assignedDevices = useMemo(() => {
    // First get only assigned devices
    const assigned = devices.filter(device => device.building !== null && device.building !== '');

    // Then apply filtering to assigned devices only
    const filtered = filterDevices(
      assigned,
      {
        distributor,
        customer,
        building,
        floor,
        area,
        hvacEquipment,
        status,
        searchString,
      },
      currentUser,
    );

    // // Apply search string filtering
    // const searchFiltered = searchString
    //   ? filtered?.filter(
    //       device =>
    //         device.name?.toLowerCase().includes(searchString.toLowerCase()) ||
    //         device.id?.toString().includes(searchString),
    //     )
    //   : filtered;

    // Sort by status (Green → Yellow → Red → Grey)
    return filtered.sort((a, b) => {
      const statusOrder = { 1: 1, 2: 2, 3: 3, 4: 4 };
      const priorityA = statusOrder[a.status] || 4;
      const priorityB = statusOrder[b.status] || 4;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return (a.name || '').localeCompare(b.name || '');
    });
  }, [devices, distributor, customer, building, floor, area, hvacEquipment, status, searchString]);
  // console.log('assignedDevices:', assignedDevices);
  // Set current page for navigation tracking (safe, no dependencies)
  useEffect(() => {
    dispatch(setCurrentPage('manage-buildings'));
  }, [dispatch]);

  // Initialize data on mount (stable)
  useEffect(() => {
    const initializeData = async () => {
      dispatch(fetchUserDevices({ devicesType: 'Sensor' }));
      // dispatch(fetchUserBuildingsDetails());
    };

    initializeData();
  }, [dispatch]);

  // Auto-refresh sensors data (stable interval)
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchUserDevices({ devicesType: 'Sensor' }));
    }, REFRESH_TIME_IN_SECONDS * 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Helper functions
  const handleSnackbarClose = () => {
    setSnackbarState({ ...snackbarState, snackbarOpen: false });
  };

  const getDisplayTitle = () => {
    return `Manage Sensors`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* ===== HEADER WITH COUNTS ===== */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <MDTypography variant="h4" sx={{ ml: 0.2, color: 'black' }}>
          {getDisplayTitle()}
        </MDTypography>

        <CustomSnackbar
          open={snackbarState.snackbarOpen}
          duration={3000}
          onClose={handleSnackbarClose}
          message="Please select a distributor and a customer"
          vertical={vertical}
          horizontal={horizontal}
        />
      </Box>

      {/* ===== MAIN CONTENT ===== */}
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box ml={1} mb={1} sx={{ width: '100%' }}>
          {/* ===== FILTER COMPONENT ===== */}
          <DevicesListFilter status={false} hvacEquipment />

          {/* ===== UNASSIGNED DEVICES ===== */}
          {currentUser.role !== 'c' && (
            <Box sx={{ mb: 3, mt: 3 }}>
              <Box
                onClick={() => setUnassignedOpen(prev => !prev)}
                sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', mb: 2 }}>
                <MDTypography variant="h5" sx={{ color: '#BE1E2D' }}>
                  Unassigned Devices ({unassignedDevices.length})
                </MDTypography>
                {unassignedOpen ? (
                  <ExpandLessIcon sx={{ color: '#BE1E2D', ml: 0.5 }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: '#BE1E2D', ml: 0.5 }} />
                )}
              </Box>
              <Collapse in={unassignedOpen} timeout={250} unmountOnExit>
                <Grid
                  className="devicesListContainer"
                  sx={{
                    display: 'flex',
                    alignItems: 'start',
                    height: 'fit-content',
                    maxHeight: '71vh',
                    overflow: 'auto',
                    width: '88vw',
                    flexWrap: 'wrap',
                    overflowX: 'visible',
                    overflowY: 'visible',
                  }}
                  margin={0}>
                  {unassignedDevices.map(device => (
                    <Grid
                      container
                      sx={{
                        lineHeight: 0,
                        position: 'relative',
                        marginBottom: '1rem',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: { xs: 'center', sm: 'start' },
                      }}
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      xl={3}
                      key={device.id}>
                      <SensorCard
                        manage
                        className="sensorCard"
                        id={device.id}
                        status={device.status}
                        name={device.name}
                        deviceType={device.device_type}
                        params={{
                          'Clean air': device.clean_air_level_avg,
                          Ventilation: device.co2_ratio_min,
                          Comfort: device.comfort_level_avg,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Box>
          )}

          {/* ===== ASSIGNED DEVICES ===== */}
          <Box sx={{ mb: 3 }}>
            {currentUser.role !== 'c' && (
              <Box
                onClick={() => setAssignedOpen(prev => !prev)}
                sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', mb: 2 }}>
                <MDTypography variant="h5" sx={{ color: '#00A551' }}>
                  Assigned Devices ({assignedDevices.length})
                </MDTypography>
                {assignedOpen ? (
                  <ExpandLessIcon sx={{ color: '#00A551', ml: 0.5 }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: '#00A551', ml: 0.5 }} />
                )}
              </Box>
            )}
            <Collapse in={assignedOpen || currentUser.role === 'c'} timeout={250} unmountOnExit>
              <Grid
                className="devicesListContainer"
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  height: 'fit-content',
                  maxHeight: '71vh',
                  overflow: 'auto',
                  width: '88vw',
                  flexWrap: 'wrap',
                  overflowX: 'visible',
                  overflowY: 'visible',
                }}
                margin={0}>
                {assignedDevices.map(device => (
                  <Grid
                    container
                    sx={{
                      lineHeight: 0,
                      position: 'relative',
                      marginBottom: '1rem',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: { xs: 'center', sm: 'start' },
                    }}
                    xs={12}
                    sm={12}
                    md={6}
                    lg={4}
                    xl={3}
                    key={device.id}>
                    <SensorCard
                      manage
                      className="sensorCard"
                      id={device.id}
                      status={device.status}
                      name={device.name}
                      building={device.building}
                      floorNumber={String(device.floor_number)}
                      areaName={device.area_name}
                      deviceType={device.device_type}
                      params={{
                        'Clean air': device.clean_air_level_avg,
                        Ventilation: device.co2_ratio_min,
                        Comfort: device.comfort_level_avg,
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Box>

          {/* ===== EMPTY STATE ===== */}
          {unassignedDevices.length === 0 && assignedDevices.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <MDTypography variant="h6" color="text">
                No devices found
              </MDTypography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const ManageSensors = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const nextNotification = location.state?.notification;
    if (!nextNotification) return;
    setNotification({ ...nextNotification, open: true });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  return (
    <DashboardLayout>
      <DashboardNavbar objectName={'Manage sensors'} />
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>
      <MDBox>
        <Grid container>
          <Grid item xs={12}>
            <ManageSensorsContent />
          </Grid>
        </Grid>
      </MDBox>
      <CustomSnackbar
        open={notification.open}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        duration={4000}
        severity={notification.severity}
        message={notification.message}
        vertical="top"
        horizontal="right"
        color="#fff"
      />
    </DashboardLayout>
  );
};

export default ManageSensors;
